import { memo, useCallback, useMemo } from "react";
import { Button, Typography } from "@/components";
import { TOKEN_CONFIG } from "@/config";
import { useDialogPortal } from "@/hooks";
import type { RedemptionPosition } from "@/services";
import { TOKEN_IDS } from "@/types";
import { getTimeRemaining } from "@/utils";
import { formatDisplayCurrency } from "@/utils/format-currency";

interface RedemptionPositionItemProps {
    position: RedemptionPosition;
}

export const RedemptionPositionItem = memo(function RedemptionPositionItem({ position }: RedemptionPositionItemProps) {
    const { openClaimRedemptionDialog } = useDialogPortal();
    const isFunded = position.status === "funded";
    const redemptionTokenSymbol = TOKEN_CONFIG[TOKEN_IDS.IUSD].symbol;
    const claimableTokenSymbol = TOKEN_CONFIG[TOKEN_IDS.USDC].symbol;
    const redemptionAmount = position.amount;
    const claimableAmount = isFunded ? Number(position.claimableAmountNormalized ?? position.amountNormalized ?? 0) : 0;
    const claimableDisplayText =
        claimableAmount > 0 ? `${formatDisplayCurrency(claimableAmount)} ${claimableTokenSymbol}` : "—";

    const claimAmount = useMemo(() => {
        if (!isFunded) {
            return 0;
        }

        return Number(position.claimableAmountNormalized ?? position.amountNormalized ?? 0);
    }, [isFunded, position.amountNormalized, position.claimableAmountNormalized]);

    const handleClaim = useCallback(() => {
        if (claimAmount <= 0) {
            return;
        }

        openClaimRedemptionDialog({
            redeemAmount: claimAmount,
        });
    }, [claimAmount, openClaimRedemptionDialog]);

    const etaInfo = useMemo(() => {
        if (position.status === "queued") {
            if (position.estimatedWaitTime > 0) {
                const estimatedCompletionTime = new Date(Date.now() + position.estimatedWaitTime).toISOString();
                return getTimeRemaining(estimatedCompletionTime, true);
            }

            return "Awaiting update";
        }

        if (position.status === "funded") {
            if (position.fundedTimestampMs && position.fundedTimestampMs > 0) {
                return `Funded on ${new Date(position.fundedTimestampMs).toLocaleDateString()}`;
            }

            return "Ready to claim";
        }

        return "Claimed";
    }, [position.estimatedWaitTime, position.fundedTimestampMs, position.status]);

    const queuePositionText = useMemo(() => {
        if (position.status === "queued" && position.queuePosition >= 0) {
            return `${position.queuePosition + 1} / ${position.totalQueueSize}`;
        }

        return "—";
    }, [position.queuePosition, position.status, position.totalQueueSize]);

    return (
        <tr className="border-t border-portfolio-positions-table transition-colors hover:bg-primary-dim">
            <td className="p-4 align-middle text-primary">
                <div className="flex items-center">
                    <Typography variant="body2" className="font-medium text-primary">
                        {formatDisplayCurrency(redemptionAmount)} {redemptionTokenSymbol}
                    </Typography>
                </div>
            </td>
            <td className="p-4 align-middle text-primary">
                <div className="flex items-center">
                    <Typography variant="body2" className="text-primary">
                        {queuePositionText}
                    </Typography>
                </div>
            </td>
            <td className="p-4 align-middle text-primary">
                <div className="flex items-center">
                    <Typography variant="body2" className="text-primary">
                        {etaInfo}
                    </Typography>
                </div>
            </td>
            <td className="p-4 align-middle text-primary">
                <div className="flex items-center">
                    <Typography variant="body2" className="text-secondary">
                        {claimableDisplayText}
                    </Typography>
                </div>
            </td>
            <td className="p-4 align-middle">
                <div className="flex items-center justify-end">
                    <Button variant="primary" size="sm" onClick={handleClaim} disabled={claimAmount <= 0}>
                        Claim USDC
                    </Button>
                </div>
            </td>
        </tr>
    );
});

RedemptionPositionItem.displayName = "RedemptionPositionItem";
